import { Timeline, TimelineItem, TimelineOppositeContent, TimelineSeparator, TimelineDot, TimelineConnector, TimelineContent } from "@mui/lab";
import { Link, Box, Chip, CircularProgress, Divider, Grid, IconButton, Tooltip, Typography, Button, Backdrop, BottomNavigation, BottomNavigationAction, Container, Paper, Alert } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate, useRevalidator } from "react-router";
import { IApiService } from "../../../../services/ApiService";
import { LogementPageOutletContext } from "../../LogementPageBase";
import { useSWState } from "../../../../contexts/SWStateContext";
import { Download, HelpOutline, NavigateBefore, NavigateNext } from "@mui/icons-material";
import labelHandiToit from "../../../../assets/label-handitoit.pdf";
import { computeHandiToitScore } from "../../../../utils/computeHandiToitScore";
import { DiagnosticLevel } from "../../../../dataModel/generated/DiagnosticLevel";

interface HistoriqueTabContentProps {
    readonly apiService: IApiService;
}

export const HistoriqueTabContent = (props: HistoriqueTabContentProps) => {
    const { logEntries, logementAccessor } = useOutletContext() as LogementPageOutletContext;
    const { state } = useSWState();
    const revalidator = useRevalidator();
    const isSyncing = state.pendingCount > 0 || revalidator.state !== 'idle';
    const [pendingDownload, setPendingDownload] = useState(false);
    const downloadBlocked = pendingDownload || isSyncing;
    // Le PDF est keyé serveur par (logementId, version) et le check `Version != version` répond
    // 404 si le front envoie une version stale. handleDownload se contente donc d'amorcer le
    // protocole : flag pendingDownload + force-revalidate. L'effet ci-dessous attend que la sync
    // soit pleinement stable (pendingCount=0 ET revalidator idle) avant de lire la version dans
    // logementAccessor — qui aura été rafraîchi via setCurrentLogement par le loader.
    const handleDownload = () => {
        if (!logementAccessor.id) {
            throw new Error("Unexpected");
        }
        if (!logementAccessor.version) {
            throw new Error("Unexpected");
        }
        setError(undefined);
        setPendingDownload(true);
        revalidator.revalidate();
    }
    const [needPdfUrl, setNeedPdfUrl] = useState<{ readonly id: number, readonly version: number} | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (pendingDownload && !isSyncing && logementAccessor.id && logementAccessor.version) {
            setPendingDownload(false);
            setNeedPdfUrl({
                id: logementAccessor.id.get(),
                version: logementAccessor.version.get()
            });
        }
    }, [pendingDownload, isSyncing, logementAccessor]);
    const navigateToPdf = useCallback(async (signal: AbortSignal) => {
        if (!needPdfUrl) {
            return;
        }
        try {
            const pdfUrl = await props.apiService.getDiagAccessiblitePdfUrl(needPdfUrl.id, needPdfUrl.version, signal);
            if (pdfUrl) {
                window.location.assign(pdfUrl);
            }
        } catch (e) {
            setError("Impossible de télécharger le fichier PDF. Vérifiez votre connexion.");
        } finally {
            setNeedPdfUrl(undefined);
        }
      }, [needPdfUrl]);
    useEffect(() => {
        if (needPdfUrl) {
            const abortController = new AbortController();
            navigateToPdf(abortController.signal);
            return () => {
                abortController.abort();
            };
        }
        return;
    }, [needPdfUrl]);
    useEffect(() => {
        if (logEntries?.some(x => x.id.startsWith('pending-')) && state.pendingCount === 0) {
            revalidator.revalidate();
        }
    }, [state.pendingCount]);
    const handiToitScore = useMemo(() => {
        return computeHandiToitScore(logementAccessor);
    }, [logementAccessor]);
    const navigate = useNavigate();
    const goToPrevious = () => {
        navigate("../documents");
    };

    if (!logEntries) {
        return null;
    }

    return (<Box sx={{ pb: 7 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Backdrop
            sx={{
                color: (theme) => theme.palette.background.default,
                zIndex: (theme) => theme.zIndex.drawer + 1
            }}
            open={needPdfUrl !== undefined}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
        <Grid container spacing={1}>
            <Grid item xs={12} textAlign={"center"}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<Download />}
                    onClick={handleDownload}
                    disabled={downloadBlocked}
                >
                    {downloadBlocked ? "Synchronisation en cours…" : "Télécharger au format Pdf"}
                </Button>
            </Grid>
            <Grid item xs={6}>
                <Box sx={{
                    alignItems: "center",
                    display: "flex"
                }}>
                    <Typography variant="body2">Notation HandiToit</Typography>
                    <Tooltip title={<>
                            <Typography>
                                La notation HandiToit permet d'évaluer pour ce logement le respect des préconisations de confort d'usage proposé par HandiToit Provence dans son cahier des charges, pour des utilisateurs en fauteuil roulant.<br/>
                                <Link color="primary.contrastText"
                                // target="_blank"
                                href={labelHandiToit}>Pour en savoir plus cliquer ici.</Link>
                            </Typography>
                        </>}>
                        <IconButton color="info">
                            <HelpOutline/>
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box>
                    {handiToitScore === DiagnosticLevel.Accessible && <Chip label="Accessible" color="info" size="small" />}
                    {handiToitScore === DiagnosticLevel.Senior && <Chip label="Senior" color="info" size="small" />}
                    {handiToitScore === DiagnosticLevel.OneEtoile && <Chip label="★" color="info" size="small" />}
                    {handiToitScore === DiagnosticLevel.TwoEtoiles && <Chip label="★★" color="info" size="small" />}
                    {handiToitScore === DiagnosticLevel.ThreeEtoiles && <Chip label="★★★" color="info" size="small" />}
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Typography sx={{pt: 1.2, pb: 0.7}} variant="body2">Statut</Typography>
                <Box>
                    {logementAccessor.complet && <Chip label={logementAccessor.complet.get() ? "Complet" : "Incomplet"} color={logementAccessor.complet.get() ? "success" : "error"} />}
                </Box>
            </Grid>
        </Grid>
        {logEntries.some(x => x.id.startsWith('pending-')) && (
            <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
                Modifications en attente de synchronisation — connexion Internet requise.
            </Alert>
        )}
        <Timeline sx={{
            padding: 0
        }}>
            {logEntries.map((x, i) => {
                const pending = x.id.startsWith('pending-');
                const comment = x.createdBy === "System" ? "Migration depuis l'ancienne base de données" : x.comment?.trim();
                const isFirstServer = !pending && i > 0 && logEntries[i - 1].id.startsWith('pending-');
                return (
                <React.Fragment key={x.id}>
                    {isFirstServer && (
                        <Box sx={{ my: 1.5 }}>
                            <Divider />
                        </Box>
                    )}
                    <TimelineItem>
                        <TimelineOppositeContent color={pending ? 'warning.main' : 'text.secondary'} sx={{
                            flexGrow: 0,
                            padding: 1,
                            paddingLeft: 0
                        }}>
                            <Typography variant="body2">{x.createdDate.toLocaleDateString()}</Typography>
                            <Typography variant="body2">{x.createdDate.toLocaleTimeString()}</Typography>
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                            <TimelineDot color={pending ? 'warning' : 'grey'} variant={pending ? 'outlined' : 'filled'} />
                            {(logEntries.length - 1 > i) && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent sx={{ padding: 1, paddingRight: 0 }}>
                            {pending ? (
                                <Typography variant="body2" color="warning.main" fontStyle="italic">Non synchronisé</Typography>
                            ) : (
                                <>
                                    {(x.createdByFirstName || x.createdByLastName) && <Typography variant="body2">{x.createdByFirstName} {x.createdByLastName}</Typography>}
                                    <Typography variant="body2">{x.createdBy}</Typography>
                                </>
                            )}
                            {comment && <Typography variant="body2">{comment}</Typography>}
                        </TimelineContent>
                    </TimelineItem>
                </React.Fragment>);})
            }
        </Timeline>
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
            <Container sx={{p: "1 1"}} maxWidth="md">
                <BottomNavigation showLabels>
                    <BottomNavigationAction label="Précédent" icon={<NavigateBefore />} onClick={goToPrevious} />
                    <BottomNavigationAction label="Suivant" icon={<NavigateNext />} sx={{visibility: "hidden"}} />
                </BottomNavigation>
            </Container>
        </Paper>
    </Box>);
}
